// ============================================================
// ChopMyShop — Browse Comparison Engine v1
//
// Pure module: no React, no Firebase, no side effects.
// Input:  pre-computed store rows (from computeStoreRows in PriceComparison)
// Output: classified rows + Penny message
//
// ── Comparison Rules v1 ──────────────────────────────────────
//
// Primary signal
//   Unit price (p/100g, p/100ml, p/item, p/L)
//   Derived from size + unitType on retailer docs — never stored in Firestore
//
// Best value
//   Store(s) with the lowest unit price among COMPARABLE rows
//   Comparable = has a valid cheapestUnitPrice (size + unitType parseable)
//
// Joint best value
//   Two or more stores share the lowest unit price (within EPSILON tolerance)
//
// Not comparable
//   Row has no unit price data — shown in UI, excluded from ranking
//   This happens when retailer docs lack size or unitType
//
// Badge rules
//   "Best value"        Shown on best-value store(s) only
//   No other badges     "Lowest pack price" is NOT a badge in v1 —
//                       pack price alone is misleading when pack sizes differ
//   One store only      No badge — nothing to compare against
//   All unit prices identical  No badge — no signal
//
// Penny copy rules
//   One winner          "Best value: Tesco at 5.3p/100g (2.5kg)."
//   Joint winners       "Best value: Tesco and Sainsbury's at 5.3p/100g (2.5kg)."
//   All tied on unit    null  — all stores equal, no signal
//   <2 comparable rows  null  — can't rank without at least two comparable stores
//   Empty rows          null
//
// ── comparisonMode (declared in taxonomy.js SUBCATEGORY_CONFIG) ──
//   Documents the intended unit for each subcategory.
//   The engine uses unitType from retailer docs for actual calculation;
//   comparisonMode is the human-readable contract declaration.
//
//   unit_100g    — weight-sold products (potatoes, cheese, butter, bread, flour…)
//   unit_100ml   — liquid-sold products (milk, oil, juice, cream…)
//   unit_litre   — large liquid packs (milk in litres, oil in litres)
//   unit_each    — per-item products (eggs, bananas, rolls…)
//   pack_only    — no meaningful unit comparison (e.g. mixed multipacks)
// ============================================================

const EPSILON = 0.0001; // pound tolerance for float unit price comparison

// ── Size display ──────────────────────────────────────────────────────────────
// Converts raw size labels to natural short display strings.
//   "2500g"  → "2.5kg"
//   "1000g"  → "1kg"
//   "500g"   → "500g"
//   "1000ml" → "1L"
//   "1500ml" → "1.5L"
//   "250ml"  → "250ml"
//   "6 pack" → "6 pack"  (pass-through — no unit match)
//   null     → null
export function formatSizeNatural(sizeLabel) {
  if (!sizeLabel) return null;
  const match = sizeLabel.match(/^(\d+(?:\.\d+)?)\s*(g|ml|kg|l)$/i);
  if (!match) return sizeLabel; // pass-through for unrecognised formats
  const val  = parseFloat(match[1]);
  const unit = match[2].toLowerCase();
  if (unit === 'g' && val >= 1000) {
    const kg = val / 1000;
    return `${kg}kg`;
  }
  if (unit === 'ml' && val >= 1000) {
    const l = val / 1000;
    return `${l}L`;
  }
  if (unit === 'l') return `${val}L`;
  return sizeLabel;
}

// ── Unit price display ────────────────────────────────────────────────────────
// up is in pounds per normalised unit (e.g. 0.053 = 5.3p/100g)
// Mirrors the formatUnitPrice function in PriceComparison.js — keep in sync.
function formatUnitPriceDisplay(pounds) {
  const pence = pounds * 100;
  if (pence < 100) {
    const rounded = Math.round(pence * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded}p`;
  }
  return `£${pounds.toFixed(2)}`;
}

// ── Core engine ───────────────────────────────────────────────────────────────
//
// buildComparisonSummary(rows, getStoreName)
//
// rows         — array of pre-computed store rows from computeStoreRows().
//                Each row must have:
//                  storeId:           string
//                  cheapestPrice:     number (pounds)
//                  cheapestUnitPrice: { up: number, label: string } | null
//                  cheapestEntry:     { siblingSize: string | null }
//
// getStoreName — fn(storeId: string) → display name string
//
// Returns:
//   {
//     rows:          ClassifiedRow[]   — input rows with badges/flags added
//     pennyMessage:  string | null     — ready-to-render Penny copy
//   }
//
// ClassifiedRow adds to each input row:
//   isBestValue:      boolean
//   isJointBestValue: boolean
//   badges:           string[]   — ["Best value"] or []
//
export function buildComparisonSummary(rows, getStoreName) {
  if (!rows || rows.length === 0) {
    return { rows: [], pennyMessage: null };
  }

  // ── 1. Identify comparable rows ───────────────────────────────────────────
  // A row is comparable when its cheapestUnitPrice is populated.
  // Rows without unit price data are included in the output but get no badge.
  const comparable = rows.filter(r => r.cheapestUnitPrice != null);

  // ── 2. Find the best (lowest) unit price across comparable rows ───────────
  // Only meaningful when there is a spread between best and worst.
  let bestUp    = null;
  let bestLabel = null;

  if (comparable.length >= 2) {
    const best  = comparable.reduce((a, b) =>
      a.cheapestUnitPrice.up < b.cheapestUnitPrice.up ? a : b
    );
    const worst = comparable.reduce((a, b) =>
      a.cheapestUnitPrice.up > b.cheapestUnitPrice.up ? a : b
    );
    const spread = worst.cheapestUnitPrice.up - best.cheapestUnitPrice.up;
    if (spread >= EPSILON) {
      bestUp    = best.cheapestUnitPrice.up;
      bestLabel = best.cheapestUnitPrice.label;
    }
    // If spread < EPSILON: all unit prices are tied → no badge, no message
  }

  // ── 3. Classify each row ──────────────────────────────────────────────────
  const winners = bestUp !== null
    ? comparable.filter(r => Math.abs(r.cheapestUnitPrice.up - bestUp) < EPSILON)
    : [];

  const classifiedRows = rows.map(row => {
    const up          = row.cheapestUnitPrice?.up ?? null;
    const isBestValue = bestUp !== null && up !== null
      && Math.abs(up - bestUp) < EPSILON;
    const isJointBestValue = isBestValue && winners.length > 1;
    return {
      ...row,
      isBestValue,
      isJointBestValue,
      badges: isBestValue ? ['Best value'] : [],
    };
  });

  // ── 4. Build Penny message ────────────────────────────────────────────────
  // Requires at least two comparable rows and a meaningful spread.
  let pennyMessage = null;
  if (bestUp !== null && winners.length >= 1) {
    const unitStr    = formatUnitPriceDisplay(bestUp);
    const unitLabel  = bestLabel || '100g';
    // Use the size from the first winner — for joint winners they share a size
    const sizeRaw    = winners[0]?.cheapestEntry?.siblingSize ?? null;
    const sizeStr    = formatSizeNatural(sizeRaw);
    const sizeParens = sizeStr ? ` (${sizeStr})` : '';

    if (winners.length === 1) {
      const name   = getStoreName(winners[0].storeId);
      pennyMessage = `Best value: ${name} at ${unitStr}/${unitLabel}${sizeParens}.`;
    } else {
      const names  = winners.map(r => getStoreName(r.storeId)).join(' and ');
      pennyMessage = `Best value: ${names} at ${unitStr}/${unitLabel}${sizeParens}.`;
    }
  }

  return { rows: classifiedRows, pennyMessage };
}
